// src/components/CalendarioReuniones/CalendarioReuniones.tsx
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { useAuth } from '../../context/AuthContext';
import { DatesSetArg, EventClickArg, EventContentArg, DateSelectArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { ChevronLeft, ChevronRight, DateRange, Event, Add } from '@mui/icons-material';
import {
  Box,
  Paper,
  Stack,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  CircularProgress,
  Alert,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Slider,
  Tooltip,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import api from '../../api/axios';

import { useClientes } from '../../hooks/useClientes';
import { useReuniones } from '../../hooks/useReuniones';
import {
  getReunionColor,
  esReunionApoyoOCounseling,
  getServicioNombre,
} from '../../hooks/useReuniones';
import { useUsuarios } from '../../hooks/useUsuarios';
import type { Reunion, FilterReunionDto } from '../../types/reuniones';
import { REUNION_TIPO_VALUES, getTipoDisplayLabel } from '../../utils/reuniones';

import FiltrosCalendario, { type StaffOption, type AlumnoOption } from './FiltrosCalendario';
import ReunionesDialog from './ReunionesDialog';
import ReunionForm from './ReunionesForm';
import HistorialReunionesServicio from './HistorialReunionesServicio';

// Importar iconos de reuniones
import iconBienvenida from '../../imagenes/Iconos/Iconos_reuniones/icon_bienvenida.svg';
import iconSeguimiento from '../../imagenes/Iconos/Iconos_reuniones/icon_seguimiento.svg';
import iconIncidencia from '../../imagenes/Iconos/Iconos_reuniones/icon_incidencia.svg';
import iconOtro from '../../imagenes/Iconos/Iconos_reuniones/icon_otro.svg';

/* ───────── helpers visuales ───────── */

// Helper para obtener el icono según el tipo de reunión
function getReunionIcon(tipo: string): string {
  const icons: Record<string, string> = {
    bienvenida: iconBienvenida,
    seguimiento: iconSeguimiento,
    incidencia: iconIncidencia,
    otro: iconOtro,
  };
  return icons[tipo] || iconOtro;
}

// Helper para obtener el color del tooltip según el tipo de reunión
function getTooltipColor(tipo: string): string {
  const colors: Record<string, string> = {
    seguimiento: '#D7EDFF',
    bienvenida: '#C2FFC4',
    incidencia: '#FFB9B4',
    otro: '#F8CDFF',
  };
  return colors[tipo] || '#F8CDFF';
}

function normalizeColor(c?: string | null): string | undefined {
  if (!c) return undefined;
  const s = String(c).trim();
  if (!s) return undefined;

  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s)) return s;
  if (/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s)) return `#${s}`;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(s)) return s;
  if (/^var\(--/.test(s)) return s;
  if (/^[a-z]+$/i.test(s)) return s;

  return undefined;
}

function getContrastYIQ(hexOrRgb: string) {
  let r: number, g: number, b: number;
  if (hexOrRgb?.startsWith?.('rgb')) {
    const rgb = hexOrRgb.match(/\d+/g);
    r = parseInt(rgb?.[0] ?? '0', 10);
    g = parseInt(rgb?.[1] ?? '0', 10);
    b = parseInt(rgb?.[2] ?? '0', 10);
  } else {
    const hc = (hexOrRgb || '#000000').replace('#', '');
    const h = hc.length >= 6 ? hc : '000000';
    r = parseInt(h.substring(0, 2), 16) || 0;
    g = parseInt(h.substring(2, 4), 16) || 0;
    b = parseInt(h.substring(4, 6), 16) || 0;
  }
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 155 ? 'black' : 'white';
}

function headerTxt(view: string, start: Dayjs, end: Dayjs, t: (k: string) => string) {
  if (view === 'dayGridMonth') return start.add(15, 'day').format('MMMM YYYY');
  if (view === 'timeGridWeek') {
    const weekLabel = t('calendar.week') || 'Semana';
    return `${weekLabel} ${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  }
  return start.format('dddd D MMM YYYY');
}

type ViewType = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

interface ReunionDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues?: any; // para no depender del tipo interno del formulario
}

interface CalendarioReunionesProps {
  soloUsuarioActual?: boolean;
}

const CalendarioReuniones: React.FC<CalendarioReunionesProps> = ({ soloUsuarioActual = false }) => {
  const { t, i18n } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { user } = useAuth();
  const staffId = user?.id != null && Number.isFinite(Number(user.id)) ? Number(user.id) : null;

  useEffect(() => {
    dayjs.locale(i18n.language.startsWith('en') ? 'en' : 'es');
  }, [i18n.language]);

  /* rango + vista */
  const [view, setView] = useState<ViewType>('timeGridWeek');
  const [range, setRange] = useState<{ start: Dayjs | null; end: Dayjs | null }>({
    start: null,
    end: null,
  });

  /* pestaña activa (calendario o historial) */
  const [activeTab, setActiveTab] = useState(0);

  /* altura de filas del calendario (fijada en 45px) */
  const rowHeight = 45; // Altura mínima fija

  /* filtros */
  const [filters, setFilters] = useState<FilterReunionDto>(() =>
    soloUsuarioActual && staffId ? { idStaff: staffId } : {},
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterReunionDto) => {
      const cleanFilters: FilterReunionDto = {};

      // Limpiar y validar valores numéricos
      if (newFilters.idStaff != null && !Number.isNaN(Number(newFilters.idStaff))) {
        cleanFilters.idStaff = Number(newFilters.idStaff);
      }

      if (newFilters.idAlumno != null && !Number.isNaN(Number(newFilters.idAlumno))) {
        cleanFilters.idAlumno = Number(newFilters.idAlumno);
      }

      if (newFilters.idCentro != null && !Number.isNaN(Number(newFilters.idCentro))) {
        cleanFilters.idCentro = Number(newFilters.idCentro);
      }

      // Preservar strings válidos (no vacíos)
      if (newFilters.tipo && typeof newFilters.tipo === 'string' && newFilters.tipo.trim() !== '') {
        cleanFilters.tipo = newFilters.tipo;
      }

      if (
        newFilters.estado &&
        typeof newFilters.estado === 'string' &&
        newFilters.estado.trim() !== ''
      ) {
        cleanFilters.estado = newFilters.estado;
      }

      if (
        newFilters.origen &&
        typeof newFilters.origen === 'string' &&
        newFilters.origen.trim() !== ''
      ) {
        cleanFilters.origen = newFilters.origen;
      }

      if (
        newFilters.search &&
        typeof newFilters.search === 'string' &&
        newFilters.search.trim() !== ''
      ) {
        cleanFilters.search = newFilters.search;
      }

      // Preservar fechas válidas
      if (
        newFilters.fechaDesde &&
        typeof newFilters.fechaDesde === 'string' &&
        newFilters.fechaDesde.trim() !== ''
      ) {
        cleanFilters.fechaDesde = newFilters.fechaDesde;
      }

      if (
        newFilters.fechaHasta &&
        typeof newFilters.fechaHasta === 'string' &&
        newFilters.fechaHasta.trim() !== ''
      ) {
        cleanFilters.fechaHasta = newFilters.fechaHasta;
      }

      // En modo "solo mis reuniones", forzar idStaff
      if (soloUsuarioActual && staffId) {
        cleanFilters.idStaff = Number(staffId);
      }

      setFilters(cleanFilters);
    },
    [soloUsuarioActual, staffId],
  );

  useEffect(() => {
    if (soloUsuarioActual && staffId) {
      setFilters((prev) => ({
        ...prev,
        idStaff: Number(staffId),
      }));
    }
  }, [soloUsuarioActual, staffId]);

  /* datos de reuniones */
  // Solo deshabilitar la query si estamos en modo "solo usuario actual" pero no tenemos staffId
  const canFetch = !soloUsuarioActual || staffId !== null;

  const { data: reuniones, isLoading, error } = useReuniones(canFetch ? filters : null);

  // Forzar actualización del calendario cuando cambien las reuniones
  useEffect(() => {
    if (calRef.current && reuniones) {
      // Pequeño delay para asegurar que los datos estén completamente procesados
      setTimeout(() => {
        calRef.current?.getApi().refetchEvents();
      }, 100);
    }
  }, [reuniones]);

  /* alumnos (clientes) */
  const { rows: clientes = [] } = useClientes();
  const alumnoOptions: AlumnoOption[] = clientes;

  /* usuarios → staff = no consultor/profesor/cliente + solo activos */
  const { rows: usuarios = [] } = useUsuarios({
    soloActivos: true,
  });

  const staffOptions: StaffOption[] = useMemo(
    () =>
      (usuarios as any[]).filter((u) => {
        const rol = String((u as any).rol ?? '').toLowerCase();
        const esStaff = rol !== 'profesor' && rol !== 'consultor' && rol !== 'cliente';
        if (!esStaff) return false;

        // En modo "solo mis reuniones" dejamos solo al propio usuario
        if (soloUsuarioActual && staffId) {
          return Number((u as any).id) === Number(staffId); // 👈 ajusta la propiedad si no es .id
        }

        return true;
      }),
    [usuarios, soloUsuarioActual, staffId],
  );

  /* ref calendario */
  const calRef = useRef<FullCalendar | null>(null);
  const nav = (fn: 'next' | 'prev') => calRef.current?.getApi()[fn]();
  const changeView = (v: ViewType | null) => v && calRef.current?.getApi().changeView(v);
  const gotoDate = (d: Dayjs | null) => {
    if (d) calRef.current?.getApi().gotoDate(d.toDate());
    setAnchor(null);
  };

  /* popover de mini calendario */
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleDatesSet = (arg: DatesSetArg) => {
    const v = arg.view.type as ViewType;
    setView(v);
    const realEnd =
      v === 'dayGridMonth' ? dayjs(arg.end).subtract(1, 'day') : dayjs(arg.end).subtract(1, 'day');
    setRange({ start: dayjs(arg.start), end: realEnd });
  };

  const header = range.start && range.end ? headerTxt(view, range.start, range.end, t) : '';

  /* reuniones → eventos */
  const events = useMemo(() => {
    if (!Array.isArray(reuniones)) return [];

    return reuniones.map((reunion: Reunion) => {
      const baseColor = normalizeColor(getReunionColor(reunion.tipo)) ?? '#1976d2';
      const textColor = getContrastYIQ(baseColor);

      const any = reunion as any;
      const staffName = any.staff?.nombre ?? any.staffNombre ?? '';
      const alumnoName = any.alumno?.nombreCompleto ?? any.alumnoNombre ?? any.clienteNombre ?? '';

      // Información de cadena
      const servicioId = (reunion as any).servicioId;
      const esParteDeCadena = servicioId && esReunionApoyoOCounseling(reunion);
      const servicioNombre = servicioId ? getServicioNombre(servicioId) : null;
      const idReunionAnterior = (reunion as any).idReunionAnterior;
      const idReunionSiguiente = (reunion as any).idReunionSiguiente;

      // Si es parte de cadena, agregar borde especial
      const borderColorFinal = esParteDeCadena ? '#2196f3' : baseColor;
      const borderWidth = esParteDeCadena ? '3px' : '1px';

      return {
        id: String(reunion.id),
        start: reunion.fechaInicio,
        end: reunion.fechaFin,
        title: '',
        backgroundColor: baseColor,
        borderColor: borderColorFinal,
        borderWidth,
        textColor,
        extendedProps: {
          ...reunion,
          staffName,
          alumnoName,
          servicioId,
          esParteDeCadena,
          servicioNombre,
          idReunionAnterior,
          idReunionSiguiente,
        },
      } as any;
    });
  }, [reuniones]);

  /* ───── estado diálogo de formulario de reunión (crear / editar) ───── */
  const [dlg, setDlg] = useState<ReunionDialogState>({
    open: false,
    mode: 'create',
  });

  const openCreate = useCallback((start: Date, end: Date) => {
    setDlg({
      open: true,
      mode: 'create',
      initialValues: {
        fecha: dayjs(start).format('YYYY-MM-DD'),
        horaInicio: dayjs(start).format('HH:mm'),
        horaFin: dayjs(end).format('HH:mm'),
      },
    });
  }, []);

  const openCreateFromButton = useCallback(() => {
    // Abrir formulario con fecha y hora actuales
    const now = dayjs();
    const end = now.add(1, 'hour');
    setDlg({
      open: true,
      mode: 'create',
      initialValues: {
        fecha: now.format('YYYY-MM-DD'),
        horaInicio: now.format('HH:mm'),
        horaFin: end.format('HH:mm'),
      },
    });
  }, []);

  const openEdit = useCallback((row: any) => {
    // Extraer fecha y hora de fechaInicio y fechaFin
    const fechaInicio = row.fechaInicio || row.fecha_inicio;
    const fechaFin = row.fechaFin || row.fecha_fin;

    // Formatear fecha (YYYY-MM-DD) y horas (HH:mm)
    const fecha = fechaInicio ? dayjs(fechaInicio).format('YYYY-MM-DD') : '';
    const horaInicio = fechaInicio ? dayjs(fechaInicio).format('HH:mm') : '';
    const horaFin = fechaFin ? dayjs(fechaFin).format('HH:mm') : '';

    setDlg({
      open: true,
      mode: 'edit',
      initialValues: {
        id: row.id, // ⭐⭐ IMPORTANTE: Pasar el ID ⭐⭐
        idStaff: row.idStaff || row.id_staff,
        idAlumno: row.idAlumno || row.id_alumno,
        idReservaOrigen: row.idReservaOrigen || row.id_reserva_origen,
        tipo: row.tipo || row.tipoReunion,
        tipoAsistentes: row.tipoAsistentes || row.tipo_asistentes,
        canal: row.canal,
        fecha,
        horaInicio,
        horaFin,
        estado: row.estado,
        origen: row.origen,
        comentariosInternos: row.comentariosInternos || row.comentarios_internos,
        comentariosPublicos: row.comentariosPublicos || row.comentarios_publicos,
        fechaEventoReferencia: row.fechaEventoReferencia || row.fecha_evento_referencia,
        tipoEventoReferencia: row.tipoEventoReferencia || row.tipo_evento_referencia,
      },
    });
  }, []);

  const closeDialog = useCallback(() => {
    setDlg((prev) => ({ ...prev, open: false }));
  }, []);

  const handleReunionSaved = useCallback(() => {
    // el propio ReunionForm invalida la query de reuniones
    setDlg((prev) => ({ ...prev, open: false }));
  }, []);

  /* ───── diálogo de detalle de reunión (ReunionesDialog) ───── */
  const [selectedReunion, setSelectedReunion] = useState<any | null>(null);
  const [openDetalle, setOpenDetalle] = useState(false);

  /* ───── tooltip hover ───── */
  const [hoveredEvent, setHoveredEvent] = useState<{
    event: any;
    anchorEl: HTMLElement;
  } | null>(null);

  const handleEventClick = (arg: EventClickArg) => {
    const ext: any = arg.event.extendedProps || {};
    const base =
      (Array.isArray(reuniones)
        ? reuniones.find((r: Reunion) => String(r.id) === arg.event.id)
        : undefined) || {};
    const combined = { ...base, ...ext, id: Number(arg.event.id) };
    setSelectedReunion(combined);
    setOpenDetalle(true);
  };

  const handleDetalleClose = () => {
    setOpenDetalle(false);
    setSelectedReunion(null);
  };

  const handleNavigateToReunion = useCallback(async (reunionId: number) => {
    try {
      // Cerrar diálogo actual
      setOpenDetalle(false);
      setSelectedReunion(null);

      // Obtener la nueva reunión
      const { data } = await api.get(`/reuniones/${reunionId}`);

      // Abrir diálogo con la nueva reunión
      setTimeout(() => {
        setSelectedReunion(data);
        setOpenDetalle(true);
      }, 100);
    } catch (error) {
      enqueueSnackbar('Error al cargar la reunión', { variant: 'error' });
    }
  }, []);

  /* ───── Ajustar ancho de columnas dinámicamente y tooltip hover ───── */
  const handleEventDidMount = useCallback((info: any) => {
    const eventEl = info.el;
    const viewType = info.view?.type;

    // Solo ajustar columnas en vistas de tiempo
    if (viewType === 'timeGridWeek' || viewType === 'timeGridDay') {
      // Encontrar el contenedor de eventos (fc-timegrid-col-events)
      const colEventsContainer = eventEl.closest('.fc-timegrid-col-events');
      if (colEventsContainer) {
        // Contar cuántos eventos hay en este contenedor
        const eventCount = colEventsContainer.querySelectorAll('.fc-timegrid-event').length;

        // Encontrar la columna padre (fc-timegrid-col)
        const colEl = eventEl.closest('.fc-timegrid-col');
        if (colEl) {
          // Ajustar el ancho mínimo de la columna según el número de eventos
          if (eventCount >= 3) {
            // Para 3 o más eventos, expandir la columna
            const minWidth = Math.max(300, eventCount * 150); // 150px por evento mínimo
            (colEl as HTMLElement).style.minWidth = `${minWidth}px`;
          } else if (eventCount === 2) {
            // Para 2 eventos, un poco más de espacio
            (colEl as HTMLElement).style.minWidth = '250px';
          }
        }

        // Ajustar tamaño de fuente según el ancho disponible del evento
        const eventWidth = eventEl.offsetWidth;
        const fontSize = Math.max(0.6, Math.min(0.75, eventWidth / 200)); // Escala entre 0.6rem y 0.75rem
        const timeLabelEl = eventEl.querySelector('span');
        if (timeLabelEl) {
          (timeLabelEl as HTMLElement).style.fontSize = `${fontSize}rem`;
        }
      }
    }

    // Añadir event listeners para el tooltip hover (funciona en todas las vistas)
    const handleMouseEnter = () => {
      setHoveredEvent({
        event: info.event,
        anchorEl: eventEl,
      });
    };

    const handleMouseLeave = () => {
      setHoveredEvent(null);
    };

    eventEl.addEventListener('mouseenter', handleMouseEnter);
    eventEl.addEventListener('mouseleave', handleMouseLeave);

    // Cleanup
    return () => {
      eventEl.removeEventListener('mouseenter', handleMouseEnter);
      eventEl.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  /* ───── render eventos ───── */
  const renderEventContent = useCallback((arg: EventContentArg) => {
    const { event } = arg;
    if (event.display === 'background') return { html: '' };

    const ext: any = event.extendedProps ?? {};
    const tipo = ext.tipo ?? '';
    const staffName = ext.staffName ?? '';
    const alumnoName = ext.alumnoName ?? '';
    const canal = ext.canal ?? '';
    const estado = ext.estado ?? '';

    const esParteDeCadena = ext.esParteDeCadena ?? false;
    const servicioNombre = ext.servicioNombre ?? null;
    const idReunionAnterior = ext.idReunionAnterior ?? null;
    const idReunionSiguiente = ext.idReunionSiguiente ?? null;

    const viewType = arg.view?.type as string | undefined;
    
    // Obtener el color de fondo del evento
    const baseColor = normalizeColor(getReunionColor(tipo)) ?? '#1976d2';
    const eventBgColor = (event as any).backgroundColor || baseColor;

    const startTime = event.start ? dayjs(event.start).format('HH:mm') : '';
    const endTime = event.end ? dayjs(event.end).format('HH:mm') : '';
    const timeLabel = !event.allDay && (startTime || endTime) ? `${startTime}–${endTime}` : '';

    const main = [tipo, staffName].filter(Boolean).join(' · ');
    const secondary = [alumnoName, canal || estado].filter(Boolean).join(' · ');

    // Agregar información de cadena al tooltip
    const cadenaInfo =
      esParteDeCadena && servicioNombre
        ? `🔗 Parte de seguimiento: ${servicioNombre}${idReunionAnterior ? ' (tiene anterior)' : ''}${idReunionSiguiente ? ' (tiene siguiente)' : ''}`
        : '';
    const tooltip = [timeLabel, main, secondary, cadenaInfo].filter(Boolean).join(' · ');

    // Obtener el icono según el tipo
    const iconPath = getReunionIcon(tipo);
    // Obtener el color del texto del evento para adaptar el icono
    const textColor = (event as any).textColor || 'white';
    // Ajustar el filtro del icono según el color del texto
    const iconFilter = textColor === 'white' ? 'brightness(0) invert(1)' : 'brightness(0)';

    if (viewType === 'timeGridWeek' || viewType === 'timeGridDay') {
      // Calcular altura del evento basándose en la duración
      let eventHeight = 45; // altura mínima
      if (event.start && event.end) {
        const start = dayjs(event.start);
        const end = dayjs(event.end);
        const durationMinutes = end.diff(start, 'minute');
        // Cada 30 minutos = 1 slot de 45px, redondeado hacia arriba
        const slots = Math.ceil(durationMinutes / 30);
        eventHeight = slots * 45;
      }
      const isCompact = eventHeight <= 45; // Diseño compacto si altura <= 45px

      const badgeCadena = esParteDeCadena
        ? `<div style="
            position:absolute;
            top:2px;
            right:2px;
            background:#2196f3;
            color:white;
            border-radius:50%;
            width:12px;
            height:12px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:8px;
            font-weight:bold;
            box-shadow:0 1px 2px rgba(0,0,0,0.2);
          " title="${servicioNombre || 'Parte de cadena'}">🔗</div>`
        : '';

      if (isCompact) {
        // Diseño compacto: solo icono centrado
        return {
          html: `
            <div
              style="
                display:flex;
                align-items:center;
                justify-content:center;
                width:100%;
                height:100%;
                position:relative;
              "
              title="${tooltip}"
            >
              ${badgeCadena}
              <img 
                src="${iconPath}" 
                alt="${tipo}" 
                style="
                  width:24px;
                  height:24px;
                  object-fit:contain;
                  filter:${iconFilter};
                "
              />
            </div>
          `,
        };
      } else {
        // Diseño completo: icono arriba, hora justo debajo
        return {
          html: `
            <div
              style="
                display:flex;
                flex-direction:column;
                align-items:center;
                justify-content:flex-start;
                width:100%;
                height:100%;
                padding:6px 4px;
                position:relative;
              "
              title="${tooltip}"
            >
              ${badgeCadena}
              <img 
                src="${iconPath}" 
                alt="${tipo}" 
                style="
                  width:28px;
                  height:28px;
                  object-fit:contain;
                  filter:${iconFilter};
                  margin-top:4px;
                "
              />
              ${
                timeLabel
                  ? `<span style="font-weight:600;white-space:nowrap;font-size:clamp(0.6rem, 2vw, 0.75rem);color:white;margin-top:4px;overflow:hidden;text-overflow:ellipsis;max-width:100%;">${timeLabel}</span>`
                  : ''
              }
            </div>
          `,
        };
      }
    }

    const text = [timeLabel, main, secondary].filter(Boolean).join(' · ');
    const badgeCadena = esParteDeCadena ? '🔗 ' : '';
    return {
      html: `
        <span
          style="
            display:flex;
            align-items:center;
            gap:4px;
            font-size:0.7rem;
            white-space:nowrap;
            overflow:hidden;
            text-overflow:ellipsis;
            position:relative;
            background-color:${eventBgColor};
            padding:2px 4px;
            border-radius:3px;
          "
          title="${tooltip}"
        >
          <img 
            src="${iconPath}" 
            alt="${tipo}" 
            style="
              width:12px;
              height:12px;
              flex-shrink:0;
              object-fit:contain;
              filter:${iconFilter};
            "
          />
          ${badgeCadena}${text}
        </span>
      `,
    };
  }, [t]);

  const handleDateClick = (arg: DateClickArg) => {
    // doble click → nueva reunión a partir de este slot
    if (arg.jsEvent.detail === 2) {
      const start = dayjs(arg.date);
      const end = start.add(1, 'hour');
      openCreate(start.toDate(), end.toDate());
    }
  };

  const handleSelect = (arg: DateSelectArg) => {
    // selección de rango para crear reunión
    openCreate(arg.start, arg.end);
  };

  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  const agendarButtonRef = useRef<HTMLElement | null>(null);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  // Observar visibilidad del botón "Agendar Reunión"
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    
    // Pequeño delay para asegurar que el botón esté renderizado
    const timer = setTimeout(() => {
      const button = agendarButtonRef.current;
      if (!button) return;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Si el botón no es visible, mostrar el botón flotante
            setShowFloatingButton(!entry.isIntersecting);
          });
        },
        {
          threshold: 0.1, // Considerar visible si al menos el 10% está visible
          rootMargin: '0px',
        }
      );

      observer.observe(button);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [activeTab]); // Re-ejecutar cuando cambie la pestaña activa

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error al cargar las reuniones: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ height: 'calc(100vh - 200px)' }}>
        {/* PESTAÑAS */}
        <Paper elevation={3} sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            <Tab label="Calendario" />
            <Tab label="Historial por Servicio" />
          </Tabs>
        </Paper>

        {/* CONTENIDO DE PESTAÑAS */}
        {activeTab === 0 && (
          <>
            {/* CABECERA + FILTROS */}
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small" onClick={() => nav('prev')}>
                  <ChevronLeft />
                </IconButton>
                <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
                  <DateRange />
                </IconButton>
                <IconButton size="small" onClick={() => nav('next')}>
                  <ChevronRight />
                </IconButton>

                <Typography variant="h6" sx={{ ml: 2, flexGrow: 1, textTransform: 'capitalize' }}>
                  {header || t('calendar.meetings.title', 'Calendario de reuniones')}
                </Typography>

                <ToggleButtonGroup
                  size="small"
                  value={view}
                  exclusive
                  onChange={(_, v) => changeView(v as ViewType)}
                  sx={{
                    gap: 0.35,
                    '& .MuiToggleButton-root': {
                      height: 35,
                      width: 64,
                      color: '#888',
                      border: '1px solid #b0b0b0',
                      borderRadius: '4px !important',
                      textTransform: 'none',
                      fontWeight: 400,
                      backgroundColor: 'transparent',
                      boxSizing: 'border-box',
                      transition: 'all 0.2s',
                      '&.Mui-selected': {
                        backgroundColor: '#21a598 !important',
                        color: '#fff !important',
                        borderColor: '#21a598',
                      },
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#21a598',
                        border: '2px solid #21a598',
                        outline: 'none',
                      },
                    },
                  }}
                >
                  <ToggleButton value="dayGridMonth">
                    {t('calendar.view.month', 'Mes')}
                  </ToggleButton>
                  <ToggleButton value="timeGridWeek">
                    {t('calendar.view.week', 'Semana')}
                  </ToggleButton>
                  <ToggleButton value="timeGridDay">{t('calendar.view.day', 'Día')}</ToggleButton>
                </ToggleButtonGroup>

              </Stack>

              <Box sx={{ mt: 2 }}>
                <FiltrosCalendario
                  onFilterChange={handleFilterChange}
                  staffOptions={staffOptions}
                  alumnoOptions={alumnoOptions}
                />
              </Box>

              {/* Leyenda por tipo de reunión */}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {REUNION_TIPO_VALUES.map((tipo) => {
                    const color = normalizeColor(getReunionColor(tipo)) ?? '#999';
                    const iconPath = getReunionIcon(tipo);
                    // Iconos más grandes para bienvenida y seguimiento, los demás en 28px
                    const iconSize = tipo === 'bienvenida' || tipo === 'seguimiento' ? '36px' : '28px';
                    return (
                      <Box key={tipo} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            bgcolor: color,
                            border: '1px solid white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        >
                          <img
                            src={iconPath}
                            alt={tipo}
                            style={{
                              width: iconSize,
                              height: iconSize,
                              objectFit: 'contain',
                              filter: 'brightness(0) invert(1)',
                            }}
                          />
                        </Box>
                        <Typography variant="body2">
                          {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Box
                  ref={(el: HTMLElement | null) => {
                    agendarButtonRef.current = el;
                  }}
                  component="span"
                  sx={{ display: 'inline-block' }}
                >
                  <Button
                    variant="contained"
                    startIcon={<Event />}
                    onClick={openCreateFromButton}
                    sx={{
                      backgroundColor: '#21a598',
                      '&:hover': {
                        backgroundColor: '#1a8578',
                      },
                    }}
                  >
                    Agendar Reunión
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* POPUP MINI CALENDARIO */}
            <Popover
              open={!!anchor}
              anchorEl={anchor}
              onClose={() => setAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <DateCalendar
                value={range.start ?? null}
                onChange={gotoDate}
                showDaysOutsideCurrentMonth
                fixedWeekNumber={6}
              />
            </Popover>

            {/* CALENDARIO PRINCIPAL */}
            <Paper
              elevation={3}
              sx={{
                p: 1,
                height: '100%',
                position: 'relative',
              }}
            >
              {isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2,
                    bgcolor: 'rgba(255,255,255,0.6)',
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              <style>{`
            #calendar-reuniones-area {
              border: 2px solid #000;
            }

            .fc-daygrid-day,
            .fc-col-header-cell {
              border: 1px solid #000 !important;
            }

            .fc-col-header-cell {
              background-color:rgb(0, 0, 0) !important;
              color: #fff !important;
              font-weight: bold;
              padding: 10px 4px !important;
            }

            .fc-timegrid .fc-timegrid-axis-cushion,
            .fc-timegrid .fc-timegrid-slot-label,
            .fc-col-header-cell-cushion {
              color: #fff !important;
              background-color:rgb(0, 0, 0) !important;
            }

            /* Formato personalizado para encabezados de días */
            .fc-col-header-cell-cushion {
              display: flex !important;
              flex-direction: column !important;
              align-items: center !important;
              justify-content: center !important;
              line-height: 1.3 !important;
              text-transform: none !important;
            }

            .fc-timegrid .fc-timegrid-slot-label {
              font-size: 0.7rem !important;
            }

            /* Altura mínima fija de 45px */
            .fc-timegrid-slot,
            .fc-timegrid-slot-minor,
            .fc-timegrid-slot {
              border-bottom: 1px solid #939393ff !important;
              min-height: ${rowHeight}px !important;
              height: ${rowHeight}px !important;
            }

            .fc-timegrid-col {
              border-right: 1px solid #939393ff !important;
            }

            .fc .fc-event .fc-event-title {
              font-size: 0.65rem !important;
              line-height: 1rem !important;
            }

            .fc-timegrid-week .fc-event,
            .fc-timegrid-week .fc-event .fc-event-title {
              font-size: 0.65rem !important;
            }

            .fc-daygrid-event {
              cursor: pointer !important;
            }

            /* Cursor pointer en todas las vistas */
            .fc-timegrid-event {
              cursor: pointer !important;
            }

            /* Asegurar que los eventos en vista de mes mantengan su color */
            .fc-daygrid-event .fc-event-title {
              color: inherit !important;
            }

            /* Asegurar que el fondo del evento se muestre correctamente en vista de mes */
            .fc-daygrid-event.fc-event {
              border: none !important;
            }

            /* Forzar que los eventos en vista de mes mantengan su backgroundColor */
            .fc-daygrid-event.fc-event {
              background-color: inherit !important;
            }
            
            /* Asegurar que el color de fondo se aplique correctamente */
            .fc-daygrid-event {
              background-color: inherit !important;
            }

            /* Estilos para eventos en vista de tiempo */
            .fc-timegrid-col-events {
              padding-left: 2px;
              padding-right: 2px;
              display: flex !important;
              flex-direction: row !important;
              gap: 4px !important;
            }

            .fc-timegrid-event {
              margin-right: 0 !important;
              border-radius: 8px !important;
              box-shadow: 0 2px 4px rgba(0,0,0,0.15) !important;
              border: none !important;
              overflow: hidden !important;
            }

            /* Distribuir eventos equitativamente - flex automático se encarga del cálculo */
            .fc-timegrid-col-events .fc-timegrid-event {
              flex: 1 1 auto !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
            }

            /* Expandir columna cuando hay múltiples eventos - usando JavaScript en eventDidMount */

            /* Asegurar que el gap se calcule correctamente */
            .fc-timegrid-col-events .fc-timegrid-event:not(:last-child) {
              margin-right: 0 !important;
            }

            /* Texto responsive dentro de los eventos */
            .fc-timegrid-event * {
              font-size: clamp(0.6rem, 1.5vw, 0.75rem) !important;
            }

            /* Ajustar iconos para que se adapten al ancho */
            .fc-timegrid-event img {
              width: clamp(20px, 3vw, 28px) !important;
              height: clamp(20px, 3vw, 28px) !important;
            }

          `}</style>

              <div
                id="calendar-reuniones-area"
                ref={calendarContainerRef}
                style={{
                  background: theme.palette.background.paper,
                  maxHeight: '1200px',
                  overflowY: 'auto',
                }}
              >
                <FullCalendar
                  ref={calRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView="timeGridWeek"
                  locale={i18n.language}
                  firstDay={1}
                  height={1200}
                  headerToolbar={false}
                  nowIndicator
                  events={events}
                  selectable
                  selectMirror
                  select={handleSelect}
                  dateClick={handleDateClick}
                  eventClick={handleEventClick}
                  datesSet={handleDatesSet}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  }}
                  allDaySlot={false}
                  slotMinTime="00:00:00"
                  slotMaxTime="23:00:00"
                  scrollTime="08:00:00"
                  scrollTimeReset={false}
                  /** duracion por 30 min*/
                  slotDuration="00:30:00"
                  slotLabelInterval="01:00"
                  eventContent={renderEventContent}
                  slotLabelFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  }}
                  eventOverlap={false}
                  slotEventOverlap={false}
                  eventDidMount={handleEventDidMount}
                  dayHeaderContent={(arg: any) => {
                    // Personalizar formato de encabezados de días
                    const date = dayjs(arg.date);
                    // Usar locale español para los días
                    const dayName = date.locale('es').format('dddd').toUpperCase();
                    const dayDate = date.format('DD/MM');
                    
                    // Retornar solo el contenido personalizado
                    return {
                      html: `
                        <div style="display:flex;flex-direction:column;align-items:center;line-height:1.3;">
                          <span style="font-size:0.75rem;font-weight:500;margin-bottom:2px;">${dayName}</span>
                          <span style="font-size:1rem;font-weight:700;">${dayDate}</span>
                        </div>
                      `,
                    };
                  }}
                />
              </div>
            </Paper>
          </>
        )}

        {activeTab === 1 && (
          <Paper elevation={3} sx={{ p: 2 }}>
            {filters.idAlumno ? (
              <HistorialReunionesServicio
                alumnoId={filters.idAlumno}
                onViewReunion={(reunion) => {
                  setSelectedReunion(reunion);
                  setOpenDetalle(true);
                  setActiveTab(0); // Cambiar a pestaña de calendario
                }}
              />
            ) : (
              <Alert severity="info">
                Por favor, selecciona un alumno en los filtros para ver el historial de reuniones
                por servicio
              </Alert>
            )}
          </Paper>
        )}
      </Box>

      {/* ───── Diálogo con ReunionForm (crear / editar) ───── */}
      <Dialog open={dlg.open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {dlg.mode === 'edit'
            ? t('reuniones.form.title.editar', 'Editar reunión')
            : t('reuniones.form.title.nueva', 'Nueva reunión')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <ReunionForm
            key={`${dlg.mode}-${dlg.initialValues?.id ?? 'new'}`}
            initialValues={dlg.initialValues}
            autoSchedule={false}
            onSaved={handleReunionSaved}
            onCancel={closeDialog}
          />
        </DialogContent>
      </Dialog>

      {/* ───── Diálogo de detalle de reunión ───── */}
      <ReunionesDialog
        open={openDetalle}
        reunion={selectedReunion}
        onClose={handleDetalleClose}
        onEdit={(row) => {
          setOpenDetalle(false);
          setSelectedReunion(null);
          openEdit(row);
        }}
        onNavigateToReunion={handleNavigateToReunion}
      />

      {/* ───── Botón flotante sticky ───── */}
      {showFloatingButton && activeTab === 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: 'white',
            borderRadius: '50px',
            padding: '8px 16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          {/* Chip con fecha */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: '#000',
              color: 'white',
              borderRadius: '50px',
              padding: '8px 16px',
              minWidth: '200px',
              justifyContent: 'center',
            }}
          >
            <IconButton
              size="small"
              onClick={() => nav('prev')}
              sx={{
                color: 'white',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                textTransform: 'capitalize',
                flex: 1,
                textAlign: 'center',
                fontSize: '0.95rem',
              }}
            >
              {header || t('calendar.meetings.title', 'Calendario de reuniones')}
            </Typography>
            <IconButton
              size="small"
              onClick={() => nav('next')}
              sx={{
                color: 'white',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>

          {/* Botón de + */}
          <IconButton
            onClick={openCreateFromButton}
            sx={{
              backgroundColor: '#21a598',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                backgroundColor: '#1a8578',
              },
            }}
          >
            <Add />
          </IconButton>
        </Box>
      )}

      {/* ───── Tooltip hover ───── */}
      {hoveredEvent && (
        <Popover
          open={!!hoveredEvent}
          anchorEl={hoveredEvent.anchorEl}
          onClose={() => setHoveredEvent(null)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          disableRestoreFocus
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              pointerEvents: 'auto',
              marginLeft: '8px', // Separación del tooltip de la reunión
            },
          }}
        >
          {(() => {
            const event = hoveredEvent.event;
            const ext: any = event.extendedProps ?? {};
            const tipo = ext.tipo ?? '';
            const staffName = ext.staffName ?? '';
            const canal = ext.canal ?? '';
            const esParteDeCadena = ext.esParteDeCadena ?? false;
            const servicioNombre = ext.servicioNombre ?? null;

            const startTime = event.start ? dayjs(event.start).format('HH:mm') : '';
            const endTime = event.end ? dayjs(event.end).format('HH:mm') : '';
            const timeLabel = startTime && endTime ? `${startTime} - ${endTime}` : '';

            const color = getTooltipColor(tipo);
            const baseColor = normalizeColor(getReunionColor(tipo)) ?? '#1976d2'; // Color original
            const iconPath = getReunionIcon(tipo);
            const tipoLabel = getTipoDisplayLabel(tipo as any);

            // Mapear canal a texto legible
            const canalLabel =
              canal === 'telefonico'
                ? 'Telefónico'
                : canal === 'online'
                  ? 'Online'
                  : canal === 'presencial'
                    ? 'Presencial'
                    : canal || '[Canal]';

            return (
              <Box
                sx={{
                  p: 1.5,
                  minWidth: 180,
                  maxWidth: 220,
                  bgcolor: color,
                  borderRadius: 2,
                  boxShadow: 3,
                  position: 'relative',
                }}
              >
                {/* Icono en la esquina superior derecha */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: baseColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  <img
                    src={iconPath}
                    alt={tipo}
                    style={{
                      width: '18px',
                      height: '18px',
                      objectFit: 'contain',
                      filter: 'brightness(0) invert(1)',
                    }}
                  />
                </Box>

                <Stack spacing={0.75} sx={{ pr: 4 }}>
                  {/* Hora */}
                  {timeLabel && (
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.8)', fontWeight: 600, fontSize: '0.75rem' }}>
                      {timeLabel}
                    </Typography>
                  )}

                  {/* Tipo */}
                  <Typography variant="subtitle2" sx={{ color: 'rgba(0,0,0,0.9)', fontWeight: 700, fontSize: '0.85rem' }}>
                    {tipoLabel}
                  </Typography>

                  {/* Canal */}
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontSize: '0.7rem' }}>
                    {canalLabel}
                  </Typography>

                  {/* Aviso de seguimiento */}
                  {esParteDeCadena && servicioNombre && (
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontSize: '0.7rem' }}>
                      Parte de seguimiento: {servicioNombre}
                    </Typography>
                  )}

                  {/* Persona del Staff */}
                  {staffName && (
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontSize: '0.7rem' }}>
                      {staffName}
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })()}
        </Popover>
      )}
    </>
  );
};

export default CalendarioReuniones;
